import React from 'react'
import { useTheme } from '@emotion/react'

import { CommonCardProps } from '../../cards/CardItem.Props'
import {
    StyledHeroCard,
    StyledLink,
    CardTextContainer,
    StyledHeroCardMedia,
    StyledHeroCardText,
    StyledCell,
} from '../../cards/HeroCard/HeroCard.styled'
import { CardByline } from '../../cards/CardByline/CardByline'

import { toLinkWithHint } from '@news-mono/web-common'
import { FundingLabel } from '../../advertising/FundingLabel/FundingLabel'
import { themedValue } from '../../__styling/themed-value'
import { FontScales } from '../../__styling/settings/fontScale'
import { MaybeLoaded } from 'json-react-layouts-data-loader'
import { FixedRatio } from '@news-mono/web-common'
import { isVideoClick } from '../../__helpers/video-card-helpers'
import { GetVideoQueue } from '../../content/Video/PlayerInterface'

import {
    getVideoQueue,
    VideoQueue,
} from '../../templates/Publication/lib/get-video-queue'
import { ComponentServices } from '@news-mono/web-common'
import { PublicationCardItem } from '@news-mono/web-common'
import { createCardClickedEvent } from '@news-mono/web-common'
import { TeaserMode } from '../CardText/CardTeaser'
import { ResponsivePictureLayout } from '../../content/Picture/ResponsivePictureLayouts'
import { useFeature } from '@news-mono/web-common'
import { getFundingType } from '../../templates/Publication/lib/get-funding-type'
import { KickerPosition } from '../Kicker/Kicker'

export type HeroVideoLayoutRatios = '1:1' | '3:1' | '1:3'

export interface HeroVideoCardProps extends CommonCardProps {
    hasBackground?: boolean
    imageLayout?: ResponsivePictureLayout
    kickerPosition?: KickerPosition
    teaserMode: TeaserMode
    /**
     * defaults to `4:3`
     */
    fixedRatio?: FixedRatio
    cardNumber: number
    item: MaybeLoaded<PublicationCardItem>
    getVideoQueue?: GetVideoQueue
    cardServices?: ComponentServices
    layoutRatio?: HeroVideoLayoutRatios
}

export const HeroVideoCard: React.FC<HeroVideoCardProps> = (props) => {
    const linkClicked = (e: React.MouseEvent<HTMLElement>) => {
        if (!props.item.loaded) {
            // We don't want the link to do anything
            e.preventDefault()
            return
        }

        if (isVideoClick(e.nativeEvent.target)) {
            e.preventDefault()
            return
        } else {
            props.onEvent(
                createCardClickedEvent(
                    props.item.result,
                    'InternalHeroCard',
                    'HeroCard',
                    props.cardNumber,
                    fundingType,
                ),
            )
        }
    }

    const returnVideoQueue = () => {
        if (!props.item.loaded) {
            return Promise.resolve<VideoQueue>({
                type: 'not-playlist-video',
                items: [],
            })
        }

        if (props.item.loaded && props.item.result.id && props.cardServices) {
            getVideoQueue(props.cardServices, props.item.result.id)
        }

        return Promise.resolve<VideoQueue>({
            type: 'not-playlist-video',
            items: [],
        })
    }

    const theme = useTheme()
    const {
        className,
        hasBackground,
        imageLayout,
        hideByline,
        teaserMode,
        onEvent,
        item,
        fixedRatio,
        layoutRatio = '1:1',
    } = props

    const showByline = theme.kind !== 'perthnow' && !hideByline
    const storyClassification = useFeature('story-classification')
    const fundingType = item.loaded
        ? getFundingType(item.result, storyClassification)
        : undefined
    const primaryTopic = item.loaded ? item.result.primaryTopic.id : undefined

    const fontScale = themedValue(theme, {
        thewest: 1.75,
        sevennews: 1.7,
        fallback: 2,
    }) as FontScales

    return (
        <StyledHeroCard
            className={className}
            data-topic={primaryTopic}
            isSponsored={!!fundingType}
            hasBackground={hasBackground}
            primaryTopic={primaryTopic}
        >
            <StyledLink
                to={toLinkWithHint(item)}
                onClick={linkClicked}
                hasBackground={hasBackground}
                isLoading={!item.loaded}
            >
                <StyledCell layoutRatio={layoutRatio}>
                    <CardTextContainer>
                        <StyledHeroCardText
                            item={item}
                            hasBackground={hasBackground}
                            kickerMode={{ size: 'large' }}
                            teaserMode={teaserMode}
                            fontScale={fontScale}
                            onEvent={onEvent}
                        />
                        {showByline && (
                            <CardByline
                                item={item}
                                onEvent={onEvent}
                                showProfile={true}
                            />
                        )}
                        {fundingType && (
                            <FundingLabel
                                hasBackground={hasBackground}
                                fundingType={fundingType}
                            />
                        )}
                    </CardTextContainer>
                </StyledCell>
                <StyledCell layoutRatio={layoutRatio}>
                    <StyledHeroCardMedia
                        isLarge
                        item={item}
                        fixedRatio={fixedRatio}
                        hasBackground={hasBackground}
                        onEvent={onEvent}
                        className={className}
                        mediaMode="heroVideoInline"
                        // Hero card should never be lazy loaded
                        disableImageLazyLoad={true}
                        willPlayVideoInline={true}
                        getVideoQueue={returnVideoQueue}
                        imageLayout={
                            imageLayout ||
                            ResponsivePictureLayout.ObjectFitContain
                        }
                        imageWidths={{
                            mobile: '100vw',
                            tablet: '100vw',
                            desktop: '628px',
                            fallbackWidth: 628,
                        }}
                    />
                </StyledCell>
            </StyledLink>
        </StyledHeroCard>
    )
}
HeroVideoCard.displayName = 'HeroVideoCard'
